import { ValidateJSModel, SSNModel } from 'forms/shared/types';
import { firstSSNPattern, middleSSNPattern, lastSSNPattern } from 'constants/patterns';
import { includesInvalidSSN } from 'constants/invalidSSNList';

function createDuplicateValidator(currentSSN, options) {
  const { applicantSSN, maritalSSN, cohabitantsList } = options;
  let duplicateError = '';
  let ssnList = [];

  if (applicantSSN?.first && applicantSSN?.middle && applicantSSN?.last) {
    ssnList.push({
      ssn: applicantSSN.first + applicantSSN.middle + applicantSSN.last,
      type: 'applicantSSN',
    });
  }

  if (options?.requireRelationshipMaritalSSN && maritalSSN?.first && maritalSSN?.middle && maritalSSN?.last) {
    ssnList.push({
      ssn: maritalSSN.first + maritalSSN.middle + maritalSSN.last,
      type: 'maritalSSN',
    });
  }

  if (options?.requireRelationshipCohabitantSSN) {
    cohabitantsList?.forEach(({ Item }) => {
      if (Item?.SSN?.first && Item?.SSN?.middle && Item?.SSN?.last) {
        ssnList.push({
          ssn: Item.SSN.first + Item.SSN.middle + Item.SSN.last,
          type: 'cohabitantSSN',
        });
      }
    });
  }

  ssnList = ssnList.filter((ssn) => ssn.ssn === `${currentSSN.first}${currentSSN.middle}${currentSSN.last}`);

  if (ssnList.length > 1) {
    // Generate proper error message
    const ssnMatches = [...new Set(ssnList.map(({ type }) => type))];

    if (['applicantSSN', 'maritalSSN', 'cohabitantSSN'].every((item) => ssnMatches.includes(item))) {
      duplicateError = 'error.ssn.duplicate.applicantMaritalCohabitant.exclusion';
    } else if (['applicantSSN', 'maritalSSN'].every((item) => ssnMatches.includes(item))) {
      duplicateError = 'error.ssn.duplicate.applicantMarital.exclusion';
    } else if (['applicantSSN', 'cohabitantSSN'].every((item) => ssnMatches.includes(item))) {
      duplicateError = 'error.ssn.duplicate.applicantCohabitant.exclusion';
    } else if (['maritalSSN', 'cohabitantSSN'].every((item) => ssnMatches.includes(item))) {
      duplicateError = 'error.ssn.duplicate.maritalCohabitant.exclusion';
    } else if (ssnMatches.includes('cohabitantsSSN')) {
      duplicateError = 'error.ssn.duplicate.cohabitants.exclusion';
    } else {
      duplicateError = 'error.ssn.duplicate.defaultMessage.exclusion';
    }

    return {
      presence: true,
      exclusion: {
        within: [currentSSN.first, currentSSN.middle, currentSSN.last],
        message: duplicateError,
      },
    };
  }

  return {};
}

const ssnModelValidator: ValidateJSModel<SSNModel> = {
  first: (value, attributes, attributeName, options) => {
    let duplicateValidator = {};
    const { first, middle, last } = attributes || {};
    if (first && middle && last) {
      duplicateValidator = createDuplicateValidator({ first, middle, last }, options);
    }

    const additional = includesInvalidSSN(first, middle, last)
      ? {
        requireTrue: { // force error message; value will never be true
          message: 'error.ssn.first.pattern'
        }
      }
      : {
        format: { pattern: firstSSNPattern, message: 'error.ssn.first.pattern' }
      };

    return {
      ...duplicateValidator,
      presence: true,
      length: { minimum: 3, tooShort: 'error.ssn.first.minimum' },
      ...additional,
    };
  },

  middle: (value, attributes, attributeName, options) => {
    let duplicateValidator = {};
    const { first, middle, last } = attributes || {};
    if (first && middle && last) {
      duplicateValidator = createDuplicateValidator({ first, middle, last }, options);
    }

    const additional = (first && !firstSSNPattern.test(first))
      || includesInvalidSSN(first, middle, last)
      ? {
        requireTrue: { // force error message; value will never be true
          message: 'error.ssn.first.pattern'
        }
      }
      : {
        format: { pattern: middleSSNPattern, message: 'error.ssn.middle.pattern' }
      };

    return {
      ...duplicateValidator,
      presence: true,
      ...additional,
    };
  },

  last: (value, attributes, attributeName, options) => {
    let duplicateValidator = {};
    const { first, middle, last } = attributes || {};
    if (first && middle && last) {
      duplicateValidator = createDuplicateValidator({ first, middle, last }, options);
    }

    const additional = (first && !firstSSNPattern.test(first))
      || includesInvalidSSN(first, middle, last)
      ? {
        requireTrue: { // force error message; value will never be true
          message: 'error.ssn.first.pattern'
        }
      }
      : {
        format: { pattern: lastSSNPattern, message: 'error.ssn.last.pattern' }
      };

    return {
      ...duplicateValidator,
      presence: true,
      ...additional,
    };
  },
  notApplicable: {},
};

export default ssnModelValidator;
